<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

import ActionOutcomeSummary from './ActionOutcomeSummary.vue'
import ConfirmStrip from './ConfirmStrip.vue'

import { useReasonText } from '../composables/useReasonText'
import { useRevertOutcome } from '../composables/useRevertOutcome'
import { cancelToken, itemToken, runToken } from '../flows/RevertFlow'
import type {
  InsightActionItemStatus,
  RunDetail,
  RunDetailItem,
  RunListRow,
} from '../models/insightAction.types'
import { useInsightActionStore } from '../stores/insightActionStore'

import { PLATFORM_ACTIONS_ROUTE } from '@/features/platform-actions'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'

const props = defineProps<{
  run: RunListRow
  label: string
  expanded: boolean
  detail: RunDetail | null
}>()
const emit = defineEmits<{
  toggle: [runId: string]
  'revert-item': [itemId: number]
  'revert-run': []
  'cancel-item': [requestId: string]
}>()

const { t } = useI18n()
const format = useFormat()
const store = useInsightActionStore()
const { itemReason, requestError, revertBlocked } = useReasonText()
const revertOutcome = useRevertOutcome(
  () => props.run.runId,
  () => props.detail,
)

const ITEM_LABEL: Partial<Record<InsightActionItemStatus, string>> = {
  succeeded: 'insightActions.history.item.succeeded',
  awaiting_approval: 'insightActions.history.item.awaitingApproval',
  reverted: 'insightActions.history.item.reverted',
  skipped: 'insightActions.history.item.skipped',
  failed: 'insightActions.history.item.failed',
  pending: 'insightActions.history.item.running',
  running: 'insightActions.history.item.running',
}
const ITEM_TONE: Partial<Record<InsightActionItemStatus, 'success' | 'info' | 'danger'>> = {
  succeeded: 'success',
  awaiting_approval: 'info',
  failed: 'danger',
}

const bar = computed(() => {
  const counts = props.run.counts
  const total = props.run.totalItems || 1
  return [
    { kind: 'applied', value: counts.succeeded, tone: 'bg-success' },
    { kind: 'waiting', value: counts.awaitingApproval, tone: 'bg-info' },
    { kind: 'skipped', value: counts.skipped, tone: 'bg-subtle' },
    { kind: 'failed', value: counts.failed, tone: 'bg-danger' },
  ]
    .filter((segment) => segment.value > 0)
    .map((segment) => ({ ...segment, pct: (segment.value / total) * 100 }))
})

const status = computed(() => {
  if (props.run.status === 'completed') {
    return { text: t('insightActions.history.status.done'), tone: 'success' as const }
  }
  if (props.run.status === 'failed') {
    return { text: t('insightActions.history.status.failed'), tone: 'danger' as const }
  }
  if (props.run.status === 'partially_completed') {
    return { text: t('insightActions.history.status.partly'), tone: 'warning' as const }
  }
  return { text: t('insightActions.history.status.running'), tone: 'info' as const }
})

const who = computed(
  () => props.run.requestedBy?.displayName || t('insightActions.history.requesterRemoved'),
)

const undoable = computed(() => (props.detail?.items ?? []).filter((item) => canUndo(item)))
const runPlatform = computed(() => platformName(props.detail?.items[0]?.platform ?? ''))

const asking = ref('')

function canUndo(item: RunDetailItem): boolean {
  return item.status === 'succeeded' && item.revertible
}

function platformName(platform: string): string {
  return platform ? platform.replaceAll('_', ' ') : t('insightActions.history.thePlatform')
}

function undoSentence(item: RunDetailItem): string {
  return t('insightActions.history.confirm.undoItemDetail', {
    what: item.summary || item.rowKey,
    where: item.campaignId
      ? t('insightActions.history.confirm.undoItemWhere', { campaign: item.campaignId })
      : '',
  })
}

function undoError(token: string): string {
  const { message, code } = requestError(store.revertErrors[token])
  return `${message} (${code})`
}

function busy(token: string): boolean {
  return store.reverting.includes(token)
}

function requestRoute(requestId: string) {
  return { name: PLATFORM_ACTIONS_ROUTE.name, query: { request: requestId } }
}

function act(run: () => void): void {
  asking.value = ''
  run()
}
</script>

<template>
  <div class="history-run-card overflow-hidden rounded-md border border-subtle">
    <button
      type="button"
      class="flex w-full items-start gap-sm px-md py-sm text-left hover:bg-surface-muted"
      :aria-expanded="expanded"
      :data-testid="`run-${run.runId}`"
      @click="emit('toggle', run.runId)"
    >
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold">{{ label }}</span>
        <span class="mt-2xs block text-2xs text-muted">
          {{
            t('insightActions.history.runSummary', {
              count: run.totalItems,
              who,
              when: format.formatDateTime(run.createdAt),
            })
          }}
        </span>
        <span class="mt-xs flex h-1.5 overflow-hidden rounded-full bg-surface-muted">
          <span
            v-for="segment in bar"
            :key="segment.kind"
            :class="segment.tone"
            :style="{ width: `${segment.pct}%` }"
          ></span>
        </span>
        <span class="mt-2xs block text-2xs text-muted">
          {{
            t('insightActions.history.runBreakdown', {
              applied: run.counts.succeeded,
              waiting: run.counts.awaitingApproval,
              skipped: run.counts.skipped,
              failed: run.counts.failed,
            })
          }}
        </span>
      </span>
      <Badge :tone="status.tone">{{ status.text }}</Badge>
    </button>

    <div v-if="expanded" class="border-t border-subtle">
      <div
        v-if="revertOutcome"
        class="border-b border-subtle px-md py-sm"
        data-testid="revert-outcome"
      >
        <ActionOutcomeSummary
          :submitted="revertOutcome.submitted"
          :buckets="revertOutcome.buckets"
        />
      </div>

      <p v-if="!detail" class="m-0 px-md py-sm text-xs text-muted">
        {{ t('insightActions.history.loading') }}
      </p>

      <template v-else>
        <div v-for="item in detail.items" :key="item.id">
          <div
            class="flex items-center gap-sm border-b border-subtle px-md py-xs text-sm last:border-0"
          >
            <span class="min-w-0 flex-1 truncate">
              <span class="font-medium">{{ item.summary || item.rowKey }}</span>
              <span v-if="item.campaignId" class="ml-xs font-mono text-2xs text-muted">{{
                item.campaignId
              }}</span>
            </span>

            <RouterLink
              v-if="item.platformActionRequestId"
              :to="requestRoute(item.platformActionRequestId)"
              class="shrink-0 text-2xs text-muted"
              :data-testid="`item-link-${item.id}`"
            >
              {{ t('insightActions.history.item.details') }}
            </RouterLink>

            <Badge :tone="ITEM_TONE[item.status] ?? 'neutral'">
              {{ ITEM_LABEL[item.status] ? t(ITEM_LABEL[item.status]!) : item.status }}
            </Badge>

            <span class="flex w-28 shrink-0 justify-end">
              <Button
                v-if="canUndo(item)"
                variant="soft"
                size="xs"
                :disabled="busy(itemToken(item.id))"
                :data-testid="`undo-${item.id}`"
                @click="asking = itemToken(item.id)"
              >
                {{ t('insightActions.history.item.undo') }}
              </Button>
              <Button
                v-else-if="
                  item.platformActionRequestId &&
                  (item.status === 'awaiting_approval' || item.cancellable)
                "
                variant="soft"
                size="xs"
                :disabled="busy(cancelToken(item.platformActionRequestId))"
                :data-testid="`cancel-${item.id}`"
                @click="asking = `ask-cancel:${item.id}`"
              >
                {{ t('insightActions.history.item.cancel') }}
              </Button>
              <RouterLink
                v-else-if="item.status === 'reverted' && item.revertRequestId"
                :to="requestRoute(item.revertRequestId)"
                class="text-2xs font-semibold"
              >
                {{ t('insightActions.history.item.undoRequest') }}
              </RouterLink>
              <span
                v-else-if="item.status === 'succeeded'"
                class="text-2xs text-muted"
                :title="revertBlocked(item.revertBlockedReason)"
                :data-testid="`no-undo-${item.id}`"
              >
                {{ t('insightActions.history.item.noUndo') }}
              </span>
              <span v-else class="truncate text-2xs text-muted" :title="item.errorMessage">
                {{ itemReason(item) }}
              </span>
            </span>
          </div>

          <p
            v-if="store.revertErrors[itemToken(item.id)]"
            class="m-0 border-b border-subtle bg-danger-soft px-md py-xs text-xs text-danger"
            :data-testid="`undo-error-${item.id}`"
          >
            {{ undoError(itemToken(item.id)) }}
          </p>
          <p
            v-if="
              item.platformActionRequestId &&
              store.revertErrors[cancelToken(item.platformActionRequestId)]
            "
            class="m-0 border-b border-subtle bg-danger-soft px-md py-xs text-xs text-danger"
            :data-testid="`cancel-error-${item.id}`"
          >
            {{ undoError(cancelToken(item.platformActionRequestId)) }}
          </p>

          <ConfirmStrip
            v-if="asking === itemToken(item.id)"
            :question="
              t('insightActions.history.confirm.undoItemQuestion', {
                platform: platformName(item.platform),
              })
            "
            :detail="undoSentence(item)"
            :confirm-label="t('insightActions.history.confirm.undoItemYes')"
            :data-testid="`confirm-undo-${item.id}`"
            @keep="asking = ''"
            @confirm="act(() => emit('revert-item', item.id))"
          />
          <ConfirmStrip
            v-if="asking === `ask-cancel:${item.id}` && item.platformActionRequestId"
            :question="t('insightActions.history.confirm.cancelQuestion')"
            :detail="
              t('insightActions.history.confirm.cancelDetail', {
                platform: platformName(item.platform),
              })
            "
            :confirm-label="t('insightActions.history.confirm.cancelYes')"
            :data-testid="`confirm-cancel-${item.id}`"
            @keep="asking = ''"
            @confirm="act(() => emit('cancel-item', item.platformActionRequestId!))"
          />
        </div>

        <div
          v-if="undoable.length"
          class="flex items-center justify-between gap-sm bg-surface-muted px-md py-xs"
        >
          <span class="text-2xs text-muted">
            {{ t('insightActions.history.undoableCount', { count: undoable.length }) }}
          </span>
          <Button
            variant="soft"
            size="xs"
            :disabled="busy(runToken(run.runId))"
            data-testid="undo-run"
            @click="asking = 'run'"
          >
            {{ t('insightActions.history.undoAll', { count: undoable.length }) }}
          </Button>
        </div>

        <p
          v-if="store.revertErrors[runToken(run.runId)]"
          class="m-0 bg-danger-soft px-md py-xs text-xs text-danger"
          data-testid="undo-run-error"
        >
          {{ undoError(runToken(run.runId)) }}
        </p>

        <ConfirmStrip
          v-if="asking === 'run'"
          :question="
            t('insightActions.history.confirm.undoRunQuestion', {
              count: undoable.length,
              platform: runPlatform,
            })
          "
          :detail="t('insightActions.history.confirm.undoRunDetail')"
          :confirm-label="t('insightActions.history.confirm.undoRunYes')"
          data-testid="confirm-undo-run"
          @keep="asking = ''"
          @confirm="act(() => emit('revert-run'))"
        />
      </template>
    </div>
  </div>
</template>
